import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useContext,
  useRef,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import apiClient, {
  bootstrapCsrfToken,
  clearSessionRefreshState,
  refreshAuthBootstrapToken,
  setSessionRefreshState,
  type AxiosError,
  type AxiosRequestConfig,
} from "@shared/services/apiClient";
import { clearLastAuthenticatedRoute } from "@shared/utils/authRouting";
import { resolveAuthErrorMessage } from "@shared/utils/authErrorTaxonomy";
import { useGlobalState } from "../hooks/useGlobalState";
import { UserContext } from "../../features/UserAccount/contexts/UserProvider";
import logger from "../utils/logger";
import {
  readStoredLoginConsentPolicyVersion,
  readStoredLoginConsentRecordId,
  readStoredLoginConsentProfile,
} from "@shared/config/loginConsentPolicy";
import { clearPersistedResumableUploadSessions } from "@shared/services/resumableEegUploadSessionStore";

export interface UserOrganizationSummary {
  organization_id: string;
  slug: string;
  name: string;
  domain?: string | null;
  is_active?: boolean;
  support_email?: string | null;
}

export interface User {
  id: string;
  organization_id?: string;
  organization?: UserOrganizationSummary | null;
  first_name: string;
  last_name: string;
  user_name: string;
  email: string;
  title_prefix?: string; // Dr., Mr., Mrs., Ms., Prof., etc.
  display_name?: string;
  role_id?: number;
  role?: string; // Add role string field
  is_active?: boolean;
  is_service_account?: boolean;
  mfa_enabled?: boolean;
  is_first_login?: boolean; // First-time login flag for password change requirement
  permissions?: string[];
  capabilities?: {
    canSignReports?: boolean;
  };
}

interface AuthContextType {
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  token: string | null;
  tokenExpiry: number | null;
  setTokenExpiry: React.Dispatch<React.SetStateAction<number | null>>;
  authCheckLoading: boolean;
  handleLogin: (
    email: string,
    password: string,
    mfaToken?: string,
    additionalData?: {
      deviceInfo?: any;
      captchaToken?: string;
      captchaVersion?: string;
      captchaProvider?: "recaptcha" | "turnstile";
    },
  ) => Promise<{
    mfaRequired?: boolean;
    captchaRequired?: boolean;
    captchaVersion?: "v2" | "v3";
    is_first_login?: boolean; // First-time login flag
  }>;
  handleLogout: () => void;
  handleChangePassword: (
    currentPassword: string,
    newPassword: string,
  ) => Promise<void>;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null; // Add error state
  pendingPasswordChange: boolean;
  clearPendingPasswordChange: () => void;
  hasPermission: (resource: string, action: string) => Promise<boolean>;
  hasAdminAccess: () => boolean; // Convenience helper for admin checks
  clearError: () => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

const BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:4000"; // Import the base URL from environment variables

function unwrapResponseData<T>(payload: any): T {
  return (payload?.data ?? payload) as T;
}

function extractPositiveInteger(value: unknown): number | null {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }
  return Math.floor(parsed);
}

function extractSessionExpiryMs(payload: unknown): number | null {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const body = payload as {
    expiresIn?: unknown;
    sessionTimeoutSemantics?: {
      idle_timeout_seconds?: unknown;
    };
    data?: {
      expiresIn?: unknown;
      sessionTimeoutSemantics?: {
        idle_timeout_seconds?: unknown;
      };
    };
  };

  const idleTimeoutSeconds = extractPositiveInteger(
    body.sessionTimeoutSemantics?.idle_timeout_seconds ??
      body.data?.sessionTimeoutSemantics?.idle_timeout_seconds,
  );
  const expiresInSeconds =
    idleTimeoutSeconds ??
    extractPositiveInteger(body.expiresIn ?? body.data?.expiresIn);

  return expiresInSeconds ? Date.now() + expiresInSeconds * 1000 : null;
}

function readHeaderValue(
  headers: Record<string, unknown> | undefined,
  name: string,
): unknown {
  if (!headers) return undefined;
  const direct = headers[name];
  if (direct !== undefined) return direct;
  const lowerName = name.toLowerCase();
  const key = Object.keys(headers).find(
    (candidate) => candidate.toLowerCase() === lowerName,
  );
  return key ? headers[key] : undefined;
}

function resolveRetryAfterSeconds(
  headers: Record<string, unknown> | undefined,
  bodyRetryAfter: unknown,
): number | undefined {
  const raw =
    readHeaderValue(headers, "Retry-After") ??
    readHeaderValue(headers, "retry-after") ??
    bodyRetryAfter;
  const value = Array.isArray(raw) ? raw[0] : raw;
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return Math.ceil(value);
  }
  if (typeof value === "string") {
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric > 0) {
      return Math.ceil(numeric);
    }
    const retryDate = Date.parse(value);
    if (Number.isFinite(retryDate)) {
      return Math.max(1, Math.ceil((retryDate - Date.now()) / 1000));
    }
  }
  return undefined;
}

function normalizeUser(raw: any): User | null {
  if (!raw || typeof raw !== "object") return null;

  const id = raw.id ?? raw.user_id;
  const email = raw.email;
  const firstName = raw.first_name ?? raw.firstName;
  const lastName = raw.last_name ?? raw.lastName;
  const userName = raw.user_name ?? raw.username;

  if (!id || !email || !firstName || !lastName || !userName) {
    return null;
  }

  const organizationCandidate = raw.organization;
  const organization =
    organizationCandidate &&
    typeof organizationCandidate === "object" &&
    typeof organizationCandidate.organization_id === "string" &&
    typeof organizationCandidate.slug === "string" &&
    typeof organizationCandidate.name === "string"
      ? {
          organization_id: organizationCandidate.organization_id,
          slug: organizationCandidate.slug,
          name: organizationCandidate.name,
          domain: organizationCandidate.domain ?? null,
          is_active: organizationCandidate.is_active,
          support_email: organizationCandidate.support_email ?? null,
        }
      : null;

  return {
    id: String(id),
    organization_id: raw.organization_id,
    organization,
    first_name: String(firstName),
    last_name: String(lastName),
    user_name: String(userName),
    email: String(email),
    title_prefix: raw.title_prefix,
    display_name: raw.display_name ?? raw.displayName,
    role_id: raw.role_id,
    role: raw.role,
    is_active: raw.is_active,
    is_service_account: raw.is_service_account ?? raw.isServiceAccount ?? false,
    mfa_enabled: raw.mfa_enabled,
    is_first_login: raw.is_first_login,
    permissions: Array.isArray(raw.permissions) ? raw.permissions : undefined,
    capabilities:
      raw.capabilities && typeof raw.capabilities === "object"
        ? {
            canSignReports: Boolean(raw.capabilities.canSignReports),
          }
        : undefined,
  };
}

interface SessionRefreshedDetail {
  tokenExpiryMs?: number | null;
}

const ACTIVE_SESSION_HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;
const ACTIVE_SESSION_ACTIVITY_WINDOW_MS = 10 * 60 * 1000;
const ACTIVE_SESSION_EXTENSION_THRESHOLD_MS = 15 * 60 * 1000;
const ACTIVE_SESSION_EXTENSION_DEBOUNCE_MS = 60 * 1000;

interface AuthBootstrapResult {
  user: User | null;
  token: string | null;
  tokenExpiry: number | null;
  status: "authenticated" | "unauthenticated" | "unavailable";
}

type AuthenticatedSessionFetchResult =
  | {
      status: "authenticated";
      session: AuthenticatedSessionSnapshot;
    }
  | {
      status: "unauthenticated";
    }
  | {
      status: "unavailable";
    };

function isPublicBootstrapRoute(pathname: string): boolean {
  return (
    pathname.startsWith("/login") ||
    pathname.startsWith("/forgot-password") ||
    pathname.startsWith("/reset-password") ||
    pathname.startsWith("/forgot-workspace") ||
    pathname.startsWith("/accept-invitation") ||
    pathname.startsWith("/shared/")
  );
}

let authBootstrapCacheKey: string | null = null;
let authBootstrapPromise: Promise<AuthBootstrapResult> | null = null;
let authBootstrapResult: AuthBootstrapResult | null = null;

function clearStoredAuthTokens(): void {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  localStorage.removeItem("tokenExpiry");
}

function clearStoredSessionStorage(): void {
  clearStoredAuthTokens();
  clearPersistedResumableUploadSessions();
}

function isTrustedLocalHttpOrigin(): boolean {
  if (typeof window === "undefined") return false;
  const { protocol, hostname } = window.location;
  return (
    protocol === "http:" &&
    (hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname.endsWith(".localhost"))
  );
}

function persistLocalFallbackSession(
  token: string | undefined,
  userData: User,
  tokenExpiry: number | null,
): void {
  if (!isTrustedLocalHttpOrigin() || !token) return;
  localStorage.setItem("token", token);
  localStorage.setItem("user", JSON.stringify(userData));
  if (tokenExpiry) {
    localStorage.setItem("tokenExpiry", String(tokenExpiry));
  }
}

function resetAuthBootstrapCache(): void {
  authBootstrapCacheKey = null;
  authBootstrapPromise = null;
  authBootstrapResult = null;
}

export function resetAuthBootstrapCacheForTests(): void {
  resetAuthBootstrapCache();
  clearSessionRefreshState();
}

interface AuthenticatedSessionSnapshot {
  user: User;
  tokenExpiry: number | null;
}

interface SessionValidationFailure {
  status?: number;
  code?: string;
  error?: string;
  message?: string;
  details?: Record<string, unknown>;
}

function normalizeSessionValidationFailure(
  status: number | undefined,
  payload: unknown,
): SessionValidationFailure | null {
  if (status === 200) {
    return null;
  }

  const body =
    payload && typeof payload === "object"
      ? (payload as {
          code?: unknown;
          error?: unknown;
          message?: unknown;
          details?: unknown;
        })
      : null;

  return {
    status,
    code: typeof body?.code === "string" ? body.code : undefined,
    error: typeof body?.error === "string" ? body.error : undefined,
    message: typeof body?.message === "string" ? body.message : undefined,
    details:
      body?.details && typeof body.details === "object"
        ? (body.details as Record<string, unknown>)
        : undefined,
  };
}

async function inspectAuthenticatedSessionFailure(): Promise<SessionValidationFailure | null> {
  try {
    const response = await apiClient.get(`/users/me`, {
      validateStatus: () => true,
    });

    return normalizeSessionValidationFailure(response.status, response.data);
  } catch {
    return null;
  }
}

function isSessionValidationUnavailable(status: number): boolean {
  return status >= 500;
}

async function fetchAuthenticatedCookieSession(
  allowRefreshOnUnauthorized: boolean,
): Promise<AuthenticatedSessionFetchResult> {
  try {
    const response = await apiClient.get(`/users/me`, {
      validateStatus: () => true,
    });

    if (isSessionValidationUnavailable(response.status)) {
      return { status: "unavailable" };
    }

    if (response.status === 401 && allowRefreshOnUnauthorized) {
      const refreshResponse = await apiClient.post("/users/refresh-token", {}, {
        validateStatus: () => true,
        _skipSessionRefresh: true,
      } as AxiosRequestConfig & { _skipSessionRefresh: boolean });

      if (isSessionValidationUnavailable(refreshResponse.status)) {
        return { status: "unavailable" };
      }

      if (refreshResponse.status !== 200) {
        return { status: "unauthenticated" };
      }

      const retryResponse = await apiClient.get(`/users/me`, {
        validateStatus: () => true,
      });

      if (isSessionValidationUnavailable(retryResponse.status)) {
        return { status: "unavailable" };
      }

      if (retryResponse.status !== 200) {
        return { status: "unauthenticated" };
      }

      const userData = normalizeUser(unwrapResponseData(retryResponse.data));
      if (!userData) {
        return { status: "unavailable" };
      }

      return {
        status: "authenticated",
        session: {
          user: userData,
          tokenExpiry:
            extractSessionExpiryMs(retryResponse.data) ??
            extractSessionExpiryMs(refreshResponse.data),
        },
      };
    }

    if (response.status !== 200) {
      return { status: "unauthenticated" };
    }

    const userData = normalizeUser(unwrapResponseData(response.data));
    if (!userData) {
      return { status: "unavailable" };
    }

    return {
      status: "authenticated",
      session: {
        user: userData,
        tokenExpiry: extractSessionExpiryMs(response.data),
      },
    };
  } catch {
    return { status: "unavailable" };
  }
}

async function bootstrapCookieSession(): Promise<AuthBootstrapResult> {
  const sessionResult = await fetchAuthenticatedCookieSession(true);
  if (sessionResult.status === "unavailable") {
    logger.warn(
      "Cookie auth check unavailable - backend did not confirm session",
    );
    return {
      user: null,
      token: null,
      tokenExpiry: null,
      status: "unavailable",
    };
  }

  if (sessionResult.status !== "authenticated") {
    clearStoredSessionStorage();
    logger.debug("Cookie auth check failed - no active session");
    return {
      user: null,
      token: null,
      tokenExpiry: null,
      status: "unauthenticated",
    };
  }

  const session = sessionResult.session;

  try {
    await bootstrapCsrfToken();
  } catch {
    // Non-fatal — CSRF cookie will be set on next successful GET
  }

  clearStoredAuthTokens();

  return {
    user: session.user,
    token: null,
    tokenExpiry: session.tokenExpiry,
    status: "authenticated",
  };
}

async function validateAuthenticatedSession(): Promise<AuthenticatedSessionSnapshot | null> {
  const sessionResult = await fetchAuthenticatedCookieSession(false);
  return sessionResult.status === "authenticated"
    ? sessionResult.session
    : null;
}

async function waitForAuthenticatedSession(
  timeoutMs = 30000,
): Promise<AuthenticatedSessionSnapshot | null> {
  const startedAt = Date.now();
  let delayMs = 100;
  do {
    const session = await validateAuthenticatedSession();
    if (session) return session;
    await new Promise((resolve) => window.setTimeout(resolve, delayMs));
    delayMs = Math.min(500, delayMs * 1.5);
  } while (Date.now() - startedAt < timeoutMs);
  return null;
}

export function getOrStartAuthBootstrap(
  pathname: string,
): Promise<AuthBootstrapResult> {
  // Anonymous public routes must not probe protected endpoints. Besides
  // producing expected-but-noisy 401s on the login page, attempting a refresh
  // here can consume rate-limit/session resources before the user signs in.
  // Authenticated state is established by handleLogin before navigation; all
  // protected routes continue to validate the cookie-backed session.
  if (isPublicBootstrapRoute(pathname)) {
    return Promise.resolve({
      user: null,
      token: null,
      tokenExpiry: null,
      status: "unauthenticated",
    });
  }

  const cacheKey = `cookie:${pathname}`;

  if (authBootstrapCacheKey === cacheKey) {
    if (authBootstrapResult) {
      return Promise.resolve(authBootstrapResult);
    }
    if (authBootstrapPromise) {
      return authBootstrapPromise;
    }
  }

  authBootstrapCacheKey = cacheKey;
  authBootstrapResult = null;
  authBootstrapPromise = bootstrapCookieSession()
    .then((result) => {
      if (authBootstrapCacheKey === cacheKey) {
        authBootstrapResult = result;
      }
      return result;
    })
    .finally(() => {
      if (authBootstrapCacheKey === cacheKey) {
        authBootstrapPromise = null;
      }
    });

  return authBootstrapPromise;
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [tokenExpiry, setTokenExpiry] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [checkedPathname, setCheckedPathname] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingPasswordChange, setPendingPasswordChange] =
    useState<boolean>(false);
  const userRef = useRef<User | null>(null);
  const tokenExpiryRef = useRef<number | null>(null);
  const sessionExtensionInFlightRef = useRef(false);
  const lastSessionExtensionAttemptRef = useRef(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { dispatch } = useGlobalState();
  const userContext = useContext(UserContext);

  if (!userContext) {
    throw new Error("AuthProvider must be used within a UserProvider");
  }

  const { setUsers } = userContext;

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    tokenExpiryRef.current = tokenExpiry;
  }, [tokenExpiry]);

  useEffect(() => {
    if (!user) {
      sessionExtensionInFlightRef.current = false;
      lastSessionExtensionAttemptRef.current = 0;
    }
  }, [user]);

  // Check authentication on initial load against the cookie-backed session.
  useEffect(() => {
    let cancelled = false;
    const pathname = location.pathname;

    const checkAuth = async () => {
      try {
        logger.debug("[AuthContext] checkAuth — bootstrapping session", {
          pathname,
          bootstrapCacheKey: authBootstrapCacheKey,
          hasCachedResult: Boolean(authBootstrapResult),
        });

        const authState = await getOrStartAuthBootstrap(pathname);
        if (cancelled) {
          return;
        }

        if (authState.user) {
          clearStoredAuthTokens();
          setError(null);
          setUser(authState.user);
          setUsers([authState.user]);
          setTokenExpiry(authState.tokenExpiry);
          setSessionRefreshState({
            isAuthenticated: true,
            tokenExpiryMs: authState.tokenExpiry,
          });
          dispatch({ type: "SET_USER", payload: authState.user });

          logger.debug("[AuthContext] Auth verified with backend", {
            userId: authState.user.id,
            email: authState.user.email,
            status: authState.status,
            tokenExpiryMs: authState.tokenExpiry,
            expiryDate: authState.tokenExpiry
              ? new Date(authState.tokenExpiry).toISOString()
              : null,
            now: new Date().toISOString(),
          });
        } else if (authState.status === "unavailable") {
          logger.warn(
            "[AuthContext] Auth verification unavailable - preserving existing session state",
            {
              pathname,
              hadAuthenticatedUser: Boolean(userRef.current),
            },
          );
        } else {
          clearStoredSessionStorage();
          setUser(null);
          setUsers([]);
          setTokenExpiry(null);
          setError(null);
          clearSessionRefreshState();
          logger.debug("[AuthContext] No authenticated session found", {
            pathname,
          });
        }
      } finally {
        if (!cancelled) {
          setCheckedPathname(pathname);
          setLoading(false);
        }
      }
    };

    void checkAuth();

    return () => {
      cancelled = true;
    };
  }, [dispatch, location.pathname, setUsers]);

  const authPendingForProtectedRoute =
    !user &&
    !isPublicBootstrapRoute(location.pathname) &&
    checkedPathname !== location.pathname;
  const authCheckLoading = checkedPathname !== location.pathname;

  useEffect(() => {
    const handleSessionRefreshed = (event: Event) => {
      const detail = (event as CustomEvent<SessionRefreshedDetail>).detail;
      if (
        detail &&
        typeof detail.tokenExpiryMs === "number" &&
        Number.isFinite(detail.tokenExpiryMs) &&
        detail.tokenExpiryMs > 0
      ) {
        setTokenExpiry(detail.tokenExpiryMs);
      }
    };

    window.addEventListener(
      "app:session-refreshed",
      handleSessionRefreshed as EventListener,
    );
    return () => {
      window.removeEventListener(
        "app:session-refreshed",
        handleSessionRefreshed as EventListener,
      );
    };
  }, []);

  // Keep the server-side sliding session expiry aligned with an actively used
  // browser tab. WebSocket traffic does not pass through auth middleware, so
  // without an authenticated HTTP heartbeat the DB session can expire while
  // the UI still appears logged in.
  useEffect(() => {
    if (!user) return;

    let lastActivityAt = Date.now();
    let heartbeatInFlight = false;
    let cancelled = false;

    const isSessionNearExpiry = () => {
      const expiry = tokenExpiryRef.current;
      return Boolean(
        expiry &&
        expiry > Date.now() &&
        expiry - Date.now() <= ACTIVE_SESSION_EXTENSION_THRESHOLD_MS,
      );
    };

    const extendActiveSession = async (reason: string) => {
      if (sessionExtensionInFlightRef.current) return;

      const now = Date.now();
      if (
        now - lastSessionExtensionAttemptRef.current <
        ACTIVE_SESSION_EXTENSION_DEBOUNCE_MS
      ) {
        return;
      }

      sessionExtensionInFlightRef.current = true;
      lastSessionExtensionAttemptRef.current = now;
      try {
        const response = await apiClient.post("/users/extend-session");
        if (cancelled) return;

        const nextExpiry = extractSessionExpiryMs(response.data);
        if (nextExpiry) {
          setTokenExpiry(nextExpiry);
          setSessionRefreshState({
            isAuthenticated: true,
            tokenExpiryMs: nextExpiry,
          });
        }
        logger.debug("[AuthContext] Active session extended", {
          reason,
          tokenExpiryMs: nextExpiry,
          expiryDate: nextExpiry ? new Date(nextExpiry).toISOString() : null,
        });
      } catch (extensionError) {
        logger.debug("[AuthContext] Active session extension failed", {
          reason,
          status: (extensionError as AxiosError)?.response?.status,
        });
      } finally {
        sessionExtensionInFlightRef.current = false;
      }
    };

    const recordActivity = () => {
      lastActivityAt = Date.now();
      if (document.visibilityState === "visible") {
        void extendActiveSession("activity");
      }
    };
    const heartbeat = async () => {
      if (
        heartbeatInFlight ||
        document.visibilityState !== "visible" ||
        Date.now() - lastActivityAt > ACTIVE_SESSION_ACTIVITY_WINDOW_MS
      ) {
        return;
      }

      heartbeatInFlight = true;
      try {
        if (isSessionNearExpiry()) {
          await extendActiveSession("heartbeat");
        } else {
          await apiClient.get("/users/me", {
            _skipSessionRefresh: false,
          } as AxiosRequestConfig & { _skipSessionRefresh: boolean });
        }
      } catch (heartbeatError) {
        logger.debug("[AuthContext] Active session heartbeat failed", {
          status: (heartbeatError as AxiosError)?.response?.status,
        });
      } finally {
        heartbeatInFlight = false;
      }
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        recordActivity();
        void heartbeat();
      }
    };
    const activityEvents = ["pointerdown", "keydown", "scroll", "touchstart"];

    activityEvents.forEach((eventName) =>
      document.addEventListener(eventName, recordActivity, { passive: true }),
    );
    document.addEventListener("visibilitychange", handleVisibilityChange);
    const interval = window.setInterval(
      () => void heartbeat(),
      ACTIVE_SESSION_HEARTBEAT_INTERVAL_MS,
    );

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      activityEvents.forEach((eventName) =>
        document.removeEventListener(eventName, recordActivity),
      );
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [user]);

  const handleLogin = async (
    email: string,
    password: string,
    mfaToken?: string,
    additionalData?: {
      deviceInfo?: any;
      captchaToken?: string;
      captchaVersion?: string;
      captchaProvider?: "recaptcha" | "turnstile";
    },
  ): Promise<{
    mfaRequired?: boolean;
    captchaRequired?: boolean;
    captchaVersion?: "v2" | "v3";
    is_first_login?: boolean;
  }> => {
    setLoading(true);
    setError(null);
    setPendingPasswordChange(false);
    try {
      const consentRecordId = readStoredLoginConsentRecordId();
      const consentPolicyVersion = readStoredLoginConsentPolicyVersion();
      const consentComplianceProfile = readStoredLoginConsentProfile();
      const response = await apiClient.post(`/users/login`, {
        email,
        password,
        mfaToken,
        ...additionalData,
        ...(consentRecordId
          ? {
              consent_record_id: consentRecordId,
              ...(consentPolicyVersion
                ? { consent_policy_version: consentPolicyVersion }
                : {}),
              ...(consentComplianceProfile
                ? { consent_compliance_profile: consentComplianceProfile }
                : {}),
            }
          : {}),
      });
      const envelope = response.data;
      const data = unwrapResponseData<any>(envelope);

      // Check if CAPTCHA is required
      if ((envelope?.code ?? data?.code) === "CAPTCHA_REQUIRED") {
        setLoading(false);
        return {
          captchaRequired: true,
          captchaVersion:
            (envelope?.captchaVersion ?? data?.captchaVersion) || "v2",
        };
      }

      // Check if MFA is required
      if (envelope?.mfaRequired ?? data?.mfaRequired) {
        setLoading(false);
        return { mfaRequired: true };
      }

      // Check if password change is required (first-time login or forced reset)
      if (envelope?.passwordChangeRequired ?? data?.passwordChangeRequired) {
        setLoading(false);
        setPendingPasswordChange(true);
        logger.info("Password change required", {
          isFirstLogin: envelope?.isFirstLogin ?? data?.isFirstLogin,
          mustChangePassword:
            envelope?.mustChangePassword ?? data?.mustChangePassword,
        });
        return { is_first_login: true };
      }

      if (data?.user) {
        const responseUserData = normalizeUser(data.user);
        if (!responseUserData) {
          setError("Invalid user profile response");
          logger.warn("Login failed - invalid user payload");
          return {};
        }

        let csrfBootstrapPromise = bootstrapCsrfToken();
        let authenticatedSession = await waitForAuthenticatedSession();
        if (!authenticatedSession) {
          resetAuthBootstrapCache();
          clearSessionRefreshState();
          csrfBootstrapPromise = refreshAuthBootstrapToken();
          await csrfBootstrapPromise.catch(() => undefined);
          authenticatedSession = await waitForAuthenticatedSession();
        }
        if (!authenticatedSession) {
          const sessionFailure = await inspectAuthenticatedSessionFailure();
          const fallbackToken =
            typeof data?.token === "string" ? data.token : undefined;
          const fallbackExpiry = extractSessionExpiryMs(response.data);
          if (isTrustedLocalHttpOrigin() && fallbackToken) {
            persistLocalFallbackSession(
              fallbackToken,
              responseUserData,
              fallbackExpiry,
            );
            authenticatedSession = {
              user: responseUserData,
              tokenExpiry: fallbackExpiry,
            };
            logger.warn(
              "Login cookie validation failed on local HTTP; using token-backed dev fallback",
              {
                email: responseUserData.email,
                status: sessionFailure?.status,
                code: sessionFailure?.code,
              },
            );
          }
        }
        if (!authenticatedSession) {
          const sessionFailure = await inspectAuthenticatedSessionFailure();
          const sessionErrorMessage =
            sessionFailure?.status === 401
              ? "Sign-in completed, but the browser did not keep the secure session cookie. Reopen the portal on its HTTPS tenant URL and try again."
              : sessionFailure
                ? resolveAuthErrorMessage(sessionFailure)
                : "Sign-in completed, but the new session could not be verified. Please try again.";

          clearStoredSessionStorage();
          clearSessionRefreshState();
          resetAuthBootstrapCache();
          setError(sessionErrorMessage);
          logger.warn("Login failed - authenticated session check failed", {
            email: responseUserData.email,
            status: sessionFailure?.status,
            code: sessionFailure?.code,
          });
          return {};
        }

        const userData = authenticatedSession.user;
        const expiry =
          authenticatedSession.tokenExpiry ??
          extractSessionExpiryMs(response.data);
        const fallbackToken =
          typeof data?.token === "string" ? data.token : undefined;
        if (isTrustedLocalHttpOrigin() && fallbackToken) {
          persistLocalFallbackSession(
            fallbackToken,
            authenticatedSession.user,
            expiry,
          );
        } else {
          clearStoredSessionStorage();
        }

        // Start CSRF bootstrap before auth state flips, but do not block the
        // post-login navigation on it. Concurrent callers reuse the same
        // deduplicated promise via ensureCsrfToken().
        void csrfBootstrapPromise;

        // Update auth state once the secure session cookie is verified.
        setUser(userData);
        setUsers([userData]);
        setTokenExpiry(expiry);
        setPendingPasswordChange(false);
        setSessionRefreshState({
          isAuthenticated: true,
          tokenExpiryMs: expiry,
        });
        authBootstrapCacheKey = `cookie:${location.pathname}`;
        authBootstrapPromise = null;
        authBootstrapResult = {
          user: userData,
          token: null,
          tokenExpiry: expiry,
          status: "authenticated",
        };
        dispatch({ type: "SET_USER", payload: userData });

        logger.info("User logged in successfully", { email: userData.email });

        // [DEBUG] Log session state right before navigation — child components
        // (e.g. MFAManagement) mount immediately after navigate() and their
        // apiClient calls depend on the session cookie + authenticatedSessionHint.
        logger.debug("[AuthContext] Post-login session state", {
          email: userData.email,
          tokenExpiryMs: expiry,
          expiryDate: expiry ? new Date(expiry).toISOString() : null,
          now: new Date().toISOString(),
          timeUntilExpiryMs: expiry ? expiry - Date.now() : null,
          navigateTarget: location.state?.from?.pathname || "/dashboard",
          cookiesPresent:
            typeof document !== "undefined"
              ? (document.cookie || "").length > 0
              : "N/A",
        });

        // Check if first-time login - if so, don't navigate (modal will handle it)
        if (userData.is_first_login) {
          setPendingPasswordChange(true);
          logger.info("First-time login detected, password change required");
          return { is_first_login: true };
        }

        // Navigate to previous page or dashboard
        const from = location.state?.from?.pathname || "/dashboard";
        navigate(from, { replace: true });

        logger.debug(
          "[AuthContext] navigate() called — child components will mount now",
        );

        return {};
      } else {
        setError("Invalid credentials");
        logger.warn("Login failed - invalid credentials");
        return {};
      }
    } catch (error: unknown) {
      let errorMessage = "Sign-in could not be completed. Please try again.";
      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as {
          response?: {
            status?: number;
            headers?: Record<string, unknown>;
            data?: {
              message?: string;
              error?: string;
              code?: string;
              retryAfter?: number | string;
              captchaVersion?: string;
              details?: Record<string, unknown>;
              isFirstLogin?: boolean;
              mustChangePassword?: boolean;
            };
          };
        };
        if (axiosError.response?.data?.code === "CAPTCHA_REQUIRED") {
          return {
            captchaRequired: true,
            captchaVersion:
              axiosError.response.data.captchaVersion === "v3" ? "v3" : "v2",
          };
        }
        const backendError = axiosError.response?.data?.error;
        if (backendError === "PASSWORD_CHANGE_REQUIRED") {
          const payload = axiosError.response?.data;
          setPendingPasswordChange(true);
          logger.info("Password change required", {
            isFirstLogin: payload?.isFirstLogin,
            mustChangePassword: payload?.mustChangePassword,
          });
          return { is_first_login: true };
        }
        errorMessage = resolveAuthErrorMessage({
          status: axiosError.response?.status,
          code: axiosError.response?.data?.code,
          error: axiosError.response?.data?.error,
          message: axiosError.response?.data?.message,
          details: axiosError.response?.data?.details,
          retryAfterSeconds: resolveRetryAfterSeconds(
            axiosError.response?.headers,
            axiosError.response?.data?.retryAfter,
          ),
        });
      }
      setError(errorMessage);
      logger.error("Login failed", {
        error: error instanceof Error ? error.message : "Unknown error",
      });
      return {};
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      // Cookie-first auth transport: logout uses httpOnly auth cookie context.
      await apiClient.post(`/users/logout`, {});
      logger.info("User logged out successfully");
    } catch (error) {
      logger.error(
        "Logout error",
        error instanceof Error ? error : new Error("Unknown logout error"),
      );
    } finally {
      // Clear localStorage
      clearStoredSessionStorage();
      clearLastAuthenticatedRoute();
      clearSessionRefreshState();
      resetAuthBootstrapCache();
      setPendingPasswordChange(false);

      // Clear local state
      setUser(null);
      setTokenExpiry(null);
      setError(null);
      navigate("/login");
    }
  };

  const handleChangePassword = async (
    currentPassword: string,
    newPassword: string,
  ) => {
    // Do not toggle AuthContext `loading` here — RequireAuth gates the entire
    // app shell on that flag, which unmounts the password form and wipes
    // success/error alerts before they can render.
    try {
      await apiClient.post(`/users/change-password`, {
        currentPassword,
        newPassword,
      });
      logger.info("Password changed successfully");
    } catch (error: unknown) {
      let errorMessage = "Change password failed";
      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as {
          response?: { data?: { message?: string; error?: string } };
        };
        errorMessage =
          axiosError.response?.data?.message ||
          axiosError.response?.data?.error ||
          "Change password failed";
      }
      logger.error("Change password failed", {
        error: error instanceof Error ? error.message : "Unknown error",
      });
      throw new Error(errorMessage);
    }
  };

  const isAuthenticated = !!user;

  /**
   * Check if user has specific permission (calls backend API)
   * Sprint 5 Item 90: Permission-based access control
   */
  const hasPermission = async (
    resource: string,
    action: string,
  ): Promise<boolean> => {
    if (!user || !isAuthenticated) {
      return false;
    }

    // SUPER_ADMIN is platform-wide and should not be blocked by missing
    // per-resource RBAC rows.
    if (user.role === "SUPER_ADMIN") {
      return true;
    }

    try {
      const encodedResource = encodeURIComponent(resource);
      const encodedAction = encodeURIComponent(action);
      const response = await apiClient.get(
        `/users/check-permission?resource=${encodedResource}&action=${encodedAction}`,
      );
      const hasPermission =
        response.data?.data?.hasPermission ?? response.data?.hasPermission;
      return hasPermission === true;
    } catch (error) {
      logger.error("Permission check failed", { resource, action, error });
      return false;
    }
  };

  /**
   * Convenience helper for admin access checks
   * Returns true if user has ADMIN or SUPER_ADMIN role
   */
  const hasAdminAccess = (): boolean => {
    const userRole = user?.role || "";
    return ["ADMIN", "SUPER_ADMIN", "ORG_ADMIN"].includes(userRole);
  };

  const clearPendingPasswordChange = () => {
    setPendingPasswordChange(false);
  };

  const clearError = () => {
    setError(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        token: null,
        tokenExpiry,
        setTokenExpiry,
        authCheckLoading,
        handleLogin,
        handleLogout,
        handleChangePassword,
        isAuthenticated,
        loading: loading || authPendingForProtectedRoute,
        error,
        pendingPasswordChange,
        clearPendingPasswordChange,
        hasPermission,
        hasAdminAccess,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the AuthContext
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
